/**
 * Resolve a janela de datas visível de uma view de calendário — a Agenda
 * (`GET /tasks?startDate&endDate`), ver contracts/ui-components.md §3 [US-4]).
 *
 * Convenção de SEMANA: Segunda a Domingo, ambos inclusive — a mesma convenção
 * usada por `current_sao_paulo_week_window()` no backend
 * (`aawz_project_management/usecase/_overview_helpers.py`, computa via
 * `today.weekday()`). Divergir faria o Overview e a Agenda contarem "semana
 * atual" de formas diferentes para o mesmo escritório.
 *
 * NÃO usamos `aawzDate().startOf('week')` aqui de propósito: a implementação
 * de `startOf`/`endOf` deste projeto (`src/utils/dates/aawz-date/startOf.js`)
 * usa `Date.prototype.getDay()` puro, ou seja, semana DOMINGO-SÁBADO — a
 * convenção correta para outros consumidores do wrapper, mas errada aqui.
 * Por isso a janela semanal é calculada localmente com aritmética de `Date`.
 *
 * Convenção de MÊS: do dia 1 ao último dia do mês de `referenceDate`,
 * inclusive.
 *
 * Convenção de DIA: `startDate === endDate === referenceDate`.
 */

const DEFAULT_VIEW_MODE = 'week'
const VALID_VIEW_MODES = new Set(['day', 'week', 'month'])

function pad2(value) {
  return String(value).padStart(2, '0')
}

// Constrói uma data "somente dia" (sem componente de hora) a partir de
// ano/mês/dia. Aceita overflow/underflow de dia (ex.: dia 0 = último dia do
// mês anterior; dia -3 = 3 dias antes do dia 1) — é assim que resolvemos
// virada de mês/ano sem lógica de calendário própria.
function dateOnly(year, month, day) {
  return new Date(year, month, day)
}

function formatIso(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

function isValidDate(value) {
  return value instanceof Date && !Number.isNaN(value.getTime())
}

// `referenceDate` ausente/inválida nunca deve produzir "Invalid Date" — cai
// para "hoje", igual ao guard equivalente de `parseTaskDate`/`aawzDate`.
function resolveReferenceDate(referenceDate) {
  return isValidDate(referenceDate) ? referenceDate : new Date()
}

// Segunda-feira da semana que contém `date`. `getDay()` é 0 (domingo) a 6
// (sábado); `(day + 6) % 7` converte para "dias desde a última segunda"
// (segunda=0 ... domingo=6).
function mondayOf(date) {
  const day = date.getDay()
  const daysSinceMonday = (day + 6) % 7
  return dateOnly(date.getFullYear(), date.getMonth(), date.getDate() - daysSinceMonday)
}

function sundayOf(monday) {
  return dateOnly(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6)
}

function firstDayOfMonth(date) {
  return dateOnly(date.getFullYear(), date.getMonth(), 1)
}

// Dia 0 do mês seguinte é o último dia do mês atual (aritmética de overflow
// do próprio `Date`) — funciona igual em meses de 28/29/30/31 dias, sem
// tabela de dias-por-mês.
function lastDayOfMonth(date) {
  return dateOnly(date.getFullYear(), date.getMonth() + 1, 0)
}

/**
 * @param {Date}   referenceDate  data de referência (a que o calendário está exibindo)
 * @param {String} viewMode       'day' | 'week' | 'month'
 * @returns {{ startDate: String, endDate: String, key: String }}  // YYYY-MM-DD; key = `${viewMode}:${startDate}`
 */
export function resolveCalendarWindow(referenceDate, viewMode) {
  const mode = VALID_VIEW_MODES.has(viewMode) ? viewMode : DEFAULT_VIEW_MODE
  const ref = resolveReferenceDate(referenceDate)

  let start
  let end
  if (mode === 'day') {
    start = dateOnly(ref.getFullYear(), ref.getMonth(), ref.getDate())
    end = start
  } else if (mode === 'month') {
    start = firstDayOfMonth(ref)
    end = lastDayOfMonth(ref)
  } else {
    start = mondayOf(ref)
    end = sundayOf(start)
  }

  const startDate = formatIso(start)
  const endDate = formatIso(end)
  return { startDate, endDate, key: `${mode}:${startDate}` }
}

/**
 * Converte a data da janela (`YYYY-MM-DD`, o formato que este módulo produz)
 * para o formato que a camada de API aceita em `GET /tasks`: `DD/MM/YYYY`.
 *
 * NÃO é cosmético. `_buildTasksQueryParams` (`api/apiTarefas.js`) só inclui
 * `startDate`/`endDate` na query quando casam `/^\d{2}\/\d{2}\/\d{4}$/`
 * (`_isCompleteDate`, o guard que evita mandar data pela metade enquanto o
 * usuário digita no filtro do board), e o backend parseia com
 * `parser_optional_date_dayfirst_v2`. Mandar ISO fazia as duas pontas da
 * janela serem DESCARTADAS EM SILÊNCIO — sem erro, sem log — e a Agenda
 * buscava a lista inteira em vez da janela visível.
 *
 * A janela continua guardada em ISO no store (`state.agendaWindow`): ISO é
 * ordenável como string e é o que o `key` de race-guard usa. A conversão
 * acontece só na saída, ao montar a query.
 *
 * Valor que não casa ISO passa adiante inalterado — quem valida é o
 * `_isCompleteDate` do lado da API.
 */
export function toApiDate(isoDate) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDate || ''))
  if (!match) return isoDate
  return `${match[3]}/${match[2]}/${match[1]}`
}
